import React from 'react';
import { useLanguage } from '../../../../../../../hooks/useLanguage';
import { pos as frPos } from '../../../../../../../locales/fr/pos';
import { pos as esPos } from '../../../../../../../locales/es/pos';
import { pos as arPos } from '../../../../../../../locales/ar/pos';

const translations = {
  fr: frPos,
  es: esPos,
  ar: arPos
};

const Receipt = ({ saleData, paymentData, onClose, onPrint }) => {
  const { lang } = useLanguage();
  const t = translations[lang];

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `${amount.toFixed(2)} €`;
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: t.checkout?.paymentMethods?.cash || 'Espèces',
      card: t.checkout?.paymentMethods?.card || 'Carte bancaire',
      check: t.checkout?.paymentMethods?.check || 'Chèque',
      transfer: t.checkout?.paymentMethods?.transfer || 'Virement',
      mobile: t.checkout?.paymentMethods?.mobile || 'Mobile Money'
    };
    return methods[method] || method;
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md mx-auto">
      {/* En-tête du ticket */}
      <div className="text-center border-b pb-4 mb-4">
        <h2 className="text-xl font-bold text-gray-800">{t.receipt?.title || 'TICKET DE CAISSE'}</h2>
        <p className="text-sm text-gray-600">{t.receipt?.saleNumber || 'Vente #'}{saleData.num}</p>
        <p className="text-sm text-gray-600">{formatDate(saleData.date)}</p>
      </div>

      {/* Informations client */}
      {saleData.customer && (
        <div className="mb-4 pb-4 border-b">
          <h3 className="font-semibold text-gray-800 mb-2">{t.receipt?.client || 'Client'}</h3>
          <p className="text-sm text-gray-700">{saleData.customer.name}</p>
          {saleData.customer.phone && (
            <p className="text-sm text-gray-600">{saleData.customer.phone}</p>
          )}
        </div>
      )}

      {/* Articles */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-800 mb-2">{t.receipt?.articles || 'Articles'}</h3>
        <div className="space-y-2">
          {saleData.items.map((item, index) => (
            <div key={index} className="flex justify-between text-sm">
              <div className="flex-1">
                <p className="font-medium">{item.name}</p>
                <p className="text-gray-600">
                  {item.quantity} x {formatCurrency(item.unitPrice)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatCurrency(item.total)}</p>
                {item.discount > 0 && (
                  <p className="text-red-600 text-xs">
                    -{formatCurrency(item.discount)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Totaux */}
      <div className="border-t pt-4 mb-4">
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span>{t.receipt?.subtotal || 'Sous-total'}:</span>
            <span>{formatCurrency(saleData.montant.ht)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>{t.checkout?.orderSummary?.tva || 'TVA'}:</span>
            <span>{formatCurrency(saleData.montant.tva)}</span>
          </div>
          {saleData.montant.discount > 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Remise:</span>
              <span>-{formatCurrency(saleData.montant.discount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>{t.receipt?.total || 'Total'}:</span>
            <span>{formatCurrency(saleData.montant.total)}</span>
          </div>
        </div>
      </div>

      {/* Informations de paiement */}
      {paymentData && (
        <div className="mb-4 pb-4 border-b">
          <h3 className="font-semibold text-gray-800 mb-2">{t.receipt?.payment || 'Paiement'}</h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>{t.receipt?.method || 'Méthode'}:</span>
              <span>{getPaymentMethodLabel(paymentData.method)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t.receipt?.amountReceived || 'Montant reçu'}:</span>
              <span>{formatCurrency(paymentData.amountReceived)}</span>
            </div>
            {paymentData.change > 0 && (
              <div className="flex justify-between">
                <span>{t.receipt?.change || 'Monnaie'}:</span>
                <span>{formatCurrency(paymentData.change)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {saleData.notes && (
        <div className="mb-4 pb-4 border-b">
          <h3 className="font-semibold text-gray-800 mb-2">{t.receipt?.notes || 'Notes'}</h3>
          <p className="text-sm text-gray-700">{saleData.notes}</p>
        </div>
      )}

      {/* Pied de page */}
      <div className="text-center text-sm text-gray-600">
        <p>{t.receipt?.thankYou || 'Merci pour votre achat !'}</p>
        <p className="mt-1">{t.receipt?.keepTicket || 'Conservez ce ticket'}</p>
      </div>

      {/* Boutons d'action */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={handlePrint}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t.checkout?.actions?.print || 'Imprimer'}
        </button>
        <button
          onClick={onClose}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
        >
          {t.checkout?.actions?.close || 'Fermer'}
        </button>
      </div>
    </div>
  );
};

export default Receipt; 